import { createAppStore } from '@app/core/stores';

const MAX_RECENT_STATIONS = 4;

interface StationsState {
  // Current: Only recent stations
  recentStationIds: string[]; // max 4 most recently used
  favoriteStationIds: string[]; // user's favorite stations

  // Future: Can add these later
  // byId: Record<string, Station>;
  // activeId: string | null;

  // Actions
  addRecentStation: (id: string) => void;
  removeRecentStation: (id: string) => void;
  clearRecentStations: () => void;
  toggleFavorite: (id: string) => void;
}

export function useIsFavorite(id: string) {
  return useStationsStore((state) => state.favoriteStationIds.includes(id));
}

/** Persisted subset of stations state (used for createAppStore second generic). */
type PersistedStationsState = Pick<StationsState, 'recentStationIds' | 'favoriteStationIds'>;

export const useStationsStore = createAppStore<StationsState, PersistedStationsState>(
  'StationsStore',
  {
    persist: {
      name: 'app.stations',
      version: 1,
      partialize: (state) => ({
        recentStationIds: state.recentStationIds,
        favoriteStationIds: state.favoriteStationIds,
      }),
    },
  },
)((set) => ({
  // Initial state
  recentStationIds: [],
  favoriteStationIds: [],

  // Add station to recent stations list
  addRecentStation: (id) => {
    set((state) => {
      // Remove station if already exists
      const filtered = state.recentStationIds.filter((stationId) => stationId !== id);

      // Add station to the beginning and limit to MAX_RECENT_STATIONS
      const updated = [id, ...filtered].slice(0, MAX_RECENT_STATIONS);

      return { recentStationIds: updated };
    });
  },

  // Remove a specific station from recent stations list
  removeRecentStation: (id) => {
    set((state) => ({
      recentStationIds: state.recentStationIds.filter((stationId) => stationId !== id),
    }));
  },

  // Clear all recent stations
  clearRecentStations: () => {
    set({
      recentStationIds: [],
    });
  },

  // Toggle favorite station
  toggleFavorite: (id) => {
    set((state) => {
      const isFavorite = state.favoriteStationIds.includes(id);
      if (isFavorite) {
        return {
          favoriteStationIds: state.favoriteStationIds.filter((stationId) => stationId !== id),
        };
      } else {
        return {
          favoriteStationIds: [...state.favoriteStationIds, id],
        };
      }
    });
  },
}));
